import React from 'react';
import PropTypes from 'prop-types';
import { observable, has } from 'mobx';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepButton from '@material-ui/core/StepButton';
import Button from '@material-ui/core/Button';
import StepLabel from '@material-ui/core/StepLabel';

import Model from '../../api/Model';
import LoadingButton from '../utils/LoadingButton';
import FormStepperIcons from "./FormStepperIcons";


export const FormContext = React.createContext(null);


@withRouter
@observer
class Form extends React.Component {
  static propTypes = {
    instance: PropTypes.instanceOf(Model).isRequired,
    redirectPath: PropTypes.func.isRequired,
  };

  @observable stepsData = [];
  @observable activeStep = 0;
  @observable lastCompletedStep = -1;
  @observable submitting = false;

  previousStep = () => {
    this.activeStep -= 1;
  };

  get isFirstStep() {
    return this.activeStep === 0;
  }

  get isLastStep() {
    return this.activeStep === this.stepsData.length - 1;
  }

  isDisabledStep(index) {
    return this.props.instance.id === null && this.lastCompletedStep < index - 1;
  }

  nextStep = async () => {
    if (this.isLastStep) {
      const { instance, history, redirectPath } = this.props;
      this.submitting = true;
      try {
        await instance.save();
      } finally {
        this.submitting = false;
      }
      history.push(redirectPath(instance));
    }
    else {
      this.activeStep += 1;
      this.lastCompletedStep += 1;
    }
  };

  get stepper() {
    return (
      <Stepper alternativeLabel nonLinear activeStep={this.activeStep}>
        {
          this.stepsData.map((stepData, index) => (
            <Step key={index}>
              <StepButton
                optional={stepData.optional}
                onClick={() => this.activeStep = index}
                completed={this.activeStep > index}
                disabled={this.isDisabledStep(index)}
                variant="outlined"
              >
                <StepLabel
                  error={stepData.fields.some(
                    fieldName => has(this.props.instance.formErrors, fieldName)
                  )}
                  StepIconComponent={FormStepperIcons}
                >
                  {stepData.label}
                </StepLabel>
              </StepButton>
            </Step>
          ))
        }
      </Stepper>
    );
  }

  get submitLabel() {
    if (this.isLastStep) {
      if (this.props.instance.id === null) {
        return 'Create';
      }
      return 'Save';
    }
    return 'Continue';
  }

  render() {
    const { instance, children } = this.props;
    if (!instance.isInitialized) {
      return null;
    }
    return (
      <FormContext.Provider value={{
        instance,
        stepsData: this.stepsData,
        activeStep: this.activeStep,
      }}>
        <Grid container direction="column" spacing={2} wrap="nowrap">
          {
            this.stepsData.length <= 1
              ? null
              : (
                <Grid item>
                  {this.stepper}
                </Grid>
              )
          }
          <Grid item>
            {children}
          </Grid>
          <Grid item>
            <Grid container spacing={2} justify="space-between">
              <Grid item>
                {
                  this.stepsData.length > 1
                    ? (
                      <Button
                        variant="outlined"
                        onClick={this.previousStep}
                        disabled={this.isFirstStep}
                      >
                        Back
                      </Button>
                    )
                    : null
                }
              </Grid>
              <Grid item>
                <LoadingButton
                  variant="outlined"
                  loading={this.submitting}
                  onClick={this.nextStep}
                >
                  {this.submitLabel}
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormContext.Provider>
    );
  }
}


export default Form;
